<template>
  <div class="list-page-container">
    <el-tabs v-model="activeTab" class="notification-tabs" @tab-click="handleTabClick">
      <el-tab-pane name="notifications">
        <span slot="label">
          <i class="el-icon-bell"></i> {{ $t('system.notificationManagement') }}
        </span>
        <div class="tab-content">
          <div class="control-panel">
            <div class="control-container">
              <div class="header-info">
                <i class="el-icon-bell"></i>
                <span>{{ $t('system.notificationManagementTips') }}</span>
              </div>
              <div class="action-wrapper">
                <el-button v-if="hasPermission('sys:notification:save')" type="primary" icon="el-icon-plus" @click="handleCreate">
                  {{ $t('system.sendNotification') }}
                </el-button>
              </div>
            </div>
          </div>

          <div class="control-panel has-query">
            <div class="control-container">
              <div class="filter-wrapper">
                <div class="filter-content">
                  <el-form :inline="true" :model="searchForm" class="filter-form">
                    <el-form-item>
                      <el-input v-model="searchForm.keyword" :placeholder="$t('system.notificationSearchPlaceholder')" prefix-icon="el-icon-search" clearable class="filter-input"></el-input>
                    </el-form-item>
                    <el-form-item>
                      <el-select v-model="searchForm.type" :placeholder="$t('system.selectNotificationType')" clearable style="width: 150px">
                        <el-option :label="$t('common.all')" value=""></el-option>
                        <el-option v-for="type in notificationTypes" :key="type.value" :label="$t(type.label)" :value="type.value"></el-option>
                      </el-select>
                    </el-form-item>
                    <el-form-item>
                      <el-select v-model="searchForm.status" :placeholder="$t('system.selectNotificationStatus')" clearable style="width: 150px">
                        <el-option :label="$t('common.all')" value=""></el-option>
                        <el-option v-for="status in notificationStatuses" :key="status.value" :label="$t(status.label)" :value="status.value"></el-option>
                      </el-select>
                    </el-form-item>
                    <el-form-item>
                      <el-select v-model="searchForm.priority" :placeholder="$t('system.selectNotificationPriority')" clearable style="width: 150px">
                        <el-option :label="$t('common.all')" value=""></el-option>
                        <el-option v-for="priority in notificationPriorities" :key="priority.value" :label="$t(priority.label)" :value="priority.value"></el-option>
                      </el-select>
                    </el-form-item>
                    <el-form-item>
                      <el-date-picker v-model="dateRange" type="daterange" :range-separator="$t('common.to')" :start-placeholder="$t('common.startDate')" :end-placeholder="$t('common.endDate')" style="width: 280px"></el-date-picker>
                    </el-form-item>
                    <el-form-item class="search-btns">
                      <el-button type="primary" @click="handleSearch" icon="el-icon-search">{{ $t('common.search') }}</el-button>
                      <el-button @click="handleReset" icon="el-icon-refresh">{{ $t('common.reset') }}</el-button>
                    </el-form-item>
                  </el-form>
                </div>
              </div>

              <div class="action-wrapper" style="margin-top: 15px;">
                <div class="action-buttons">
                  <el-badge :value="unreadCount" class="unread-badge" :hidden="unreadCount === 0" style="margin-right: 15px">
                    <el-button type="text" @click="handleViewUnreadOnly" size="small">
                      <i class="el-icon-bell"></i> {{ $t('system.unreadNotifications') }}
                    </el-button>
                  </el-badge>
                  <el-dropdown v-if="hasBatchOperationPermission" :disabled="!hasSelectedNotifications">
                    <el-button type="warning" :disabled="!hasSelectedNotifications" size="small">
                      <i class="el-icon-more"></i> {{ $t('system.batchOperations') }} <i class="el-icon-arrow-down el-icon--right"></i>
                    </el-button>
                    <el-dropdown-menu slot="dropdown">
                      <el-dropdown-item v-if="hasPermission('sys:notification:update')" @click.native="handleBatchMarkAsRead">
                        <i class="el-icon-check"></i> {{ $t('system.markAsRead') }}
                      </el-dropdown-item>
                      <el-dropdown-item v-if="hasPermission('sys:notification:delete')" @click.native="handleBatchDelete">
                        <i class="el-icon-delete"></i> {{ $t('common.delete') }}
                      </el-dropdown-item>
                    </el-dropdown-menu>
                  </el-dropdown>
                </div>
              </div>
            </div>
          </div>

          <el-card class="list-card">
            <el-table :data="notifications" v-loading="loading" height="100%" style="width: 100%" class="custom-table" @selection-change="handleSelectionChange" :element-loading-text="$t('common.loading')">
              <el-table-column type="selection" width="55" align="center"></el-table-column>
              <el-table-column prop="type" :label="$t('system.notificationType')" width="150" align="center">
                <template slot-scope="scope">
                  <el-tag :type="getNotificationTypeTagType(scope.row.type)" effect="dark" size="mini">{{ $t(getNotificationTypeKey(scope.row.type)) }}</el-tag>
                </template>
              </el-table-column>
              <el-table-column prop="title" :label="$t('system.notificationTitle')" min-width="200">
                <template slot-scope="scope">
                  <div class="notification-title" @click="handleViewDetail(scope.row)">
                    <span :class="{ 'unread': scope.row.status === 'unread' }">{{ scope.row.title }}</span>
                    <el-tag v-if="scope.row.status === 'unread'" size="mini" type="danger" style="margin-left: 5px">{{ $t('system.unreadNotification') }}</el-tag>
                  </div>
                </template>
              </el-table-column>
              <el-table-column prop="content" :label="$t('system.notificationContent')" min-width="300" show-overflow-tooltip>
                <template slot-scope="scope">
                  <div class="notification-content" @click="handleViewDetail(scope.row)">{{ scope.row.content }}</div>
                </template>
              </el-table-column>
              <el-table-column prop="sender" :label="$t('system.sender')" width="120" align="center"></el-table-column>
              <el-table-column prop="recipient" :label="$t('system.recipient')" width="120" align="center">
                <template slot-scope="scope">{{ scope.row.recipient || $t('system.allUsers') }}</template>
              </el-table-column>
              <el-table-column prop="channels" :label="$t('system.notificationChannels')" min-width="200" align="center">
                <template slot-scope="scope">
                  <el-tag v-for="channel in parseChannels(scope.row.channels)" :key="channel" size="mini" style="margin-right: 5px">{{ $t(getChannelKey(channel)) }}</el-tag>
                </template>
              </el-table-column>
              <el-table-column prop="priority" :label="$t('system.notificationPriority')" width="120" align="center">
                <template slot-scope="scope">
                  <el-tag :type="getPriorityTagType(scope.row.priority)" effect="dark" size="mini">{{ $t(getPriorityKey(scope.row.priority)) }}</el-tag>
                </template>
              </el-table-column>
              <el-table-column prop="status" :label="$t('common.status')" width="100" align="center">
                <template slot-scope="scope">
                  <el-tag :type="getNotificationStatusTagType(scope.row.status)" effect="dark" size="mini">{{ $t(getNotificationStatusKey(scope.row.status)) }}</el-tag>
                </template>
              </el-table-column>
              <el-table-column prop="createDate" :label="$t('system.createdAt')" width="180" align="center">
                <template slot-scope="scope">{{ formatDate(scope.row.createDate) }}</template>
              </el-table-column>
              <el-table-column v-if="hasAnyNotificationAction" :label="$t('common.operation')" :width="notificationActionWidth" align="center" fixed="right">
                <template slot-scope="scope">
                  <div class="action-buttons">
                    <el-button v-if="hasPermission('sys:notification:update')" size="mini" type="warning" plain @click="handleEditNotification(scope.row)" :disabled="scope.row.status === 'deleted'" icon="el-icon-edit">{{ $t('common.edit') }}</el-button>
                    <el-button v-if="hasPermission('sys:notification:info')" size="mini" type="primary" plain @click="handleViewDetail(scope.row)" :disabled="scope.row.status === 'deleted'" icon="el-icon-view">{{ $t('common.detail') }}</el-button>
                    <el-button v-if="hasPermission('sys:notification:update')" size="mini" class="btn-action-status" plain @click="handleMarkAsRead(scope.row)" :disabled="scope.row.status === 'read' || scope.row.status === 'deleted'" icon="el-icon-check">{{ $t('system.markAsRead') }}</el-button>
                    <el-button v-if="hasPermission('sys:notification:delete')" size="mini" type="danger" plain @click="handleDelete(scope.row)" :disabled="scope.row.status === 'deleted'" icon="el-icon-delete">{{ $t('common.delete') }}</el-button>
                  </div>
                </template>
              </el-table-column>
            </el-table>
            <div class="notification-list-pagination">
              <el-pagination @size-change="handleSizeChange" @current-change="handleCurrentChange" :current-page="pagination.currentPage" :page-sizes="[10, 20, 50, 100]" :page-size="pagination.pageSize" layout="total, sizes, prev, pager, next, jumper" :total="pagination.total"></el-pagination>
            </div>
          </el-card>
        </div>
      </el-tab-pane>

      <el-tab-pane name="templates">
        <span slot="label">
          <i class="el-icon-document-copy"></i> {{ $t('system.notificationTemplate') }}
        </span>
        <div class="tab-content">
          <div class="control-panel">
            <div class="control-container">
              <div class="header-info">
                <i class="el-icon-document-copy"></i>
                <span>{{ $t('system.notificationTemplateTips') }}</span>
              </div>
              <div class="action-wrapper">
                <el-button type="primary" @click="handleCreateTemplate" icon="el-icon-plus" v-if="hasPermission('sys:notification:template:save')">{{ $t('system.createTemplate') }}</el-button>
                <el-button type="success" @click="handleBatchEnableTemplates" :disabled="selectedTemplateIds.length === 0" icon="el-icon-check" v-if="hasPermission('sys:notification:template:update')">{{ $t('system.enableSelected') }}</el-button>
                <el-button type="warning" @click="handleBatchDisableTemplates" :disabled="selectedTemplateIds.length === 0" icon="el-icon-close" v-if="hasPermission('sys:notification:template:update')">{{ $t('system.disableSelected') }}</el-button>
                <el-button type="danger" @click="handleBatchDeleteTemplates" :disabled="selectedTemplateIds.length === 0" icon="el-icon-delete" v-if="hasPermission('sys:notification:template:delete')">{{ $t('system.deleteSelected') }}</el-button>
              </div>
            </div>
          </div>

          <el-card class="list-card">
            <el-table :data="templateList" v-loading="templateLoading" height="100%" style="width: 100%" class="custom-table" :element-loading-text="$t('common.loading')" @selection-change="handleTemplateSelectionChange">
              <el-table-column type="selection" width="55" align="center"></el-table-column>
              <el-table-column prop="name" :label="$t('system.templateName')" width="180" align="center"></el-table-column>
              <el-table-column prop="type" :label="$t('common.type')" width="120" align="center">
                <template slot-scope="scope">
                  <el-tag type="info" size="small" effect="plain">{{ $t(getTemplateTypeText(scope.row.type)) }}</el-tag>
                </template>
              </el-table-column>
              <el-table-column prop="content" :label="$t('common.content')" min-width="300" align="left">
                <template slot-scope="scope">
                  <div class="template-content-preview">{{ scope.row.content }}</div>
                </template>
              </el-table-column>
              <el-table-column prop="enabled" :label="$t('common.status')" width="100" align="center">
                <template slot-scope="scope">
                  <el-switch v-model="scope.row.enabledBool" active-color="#13ce66" inactive-color="#ff4949" size="small" @change="handleToggleTemplateEnable(scope.row)" :disabled="!hasPermission('sys:notification:template:update')"></el-switch>
                </template>
              </el-table-column>
              <el-table-column prop="channels" :label="$t('system.notificationChannels')" min-width="180" align="center">
                <template slot-scope="scope">
                  <div class="channel-tags">
                    <el-tag v-for="channel in parseChannels(scope.row.channels)" :key="channel" size="mini" effect="light" class="channel-tag">{{ $t(getChannelKey(channel)) }}</el-tag>
                  </div>
                </template>
              </el-table-column>
              <el-table-column prop="createDate" :label="$t('common.createTime')" width="160" align="center">
                <template slot-scope="scope">{{ formatDate(scope.row.createDate) }}</template>
              </el-table-column>
              <el-table-column v-if="hasAnyTemplateAction" :label="$t('common.operation')" :width="templateActionWidth" align="center" fixed="right">
                <template slot-scope="scope">
                  <div class="action-buttons">
                    <el-button type="warning" size="mini" plain icon="el-icon-edit" @click="handleEditTemplate(scope.row)" v-if="hasPermission('sys:notification:template:update')">{{ $t('common.edit') }}</el-button>
                    <el-button type="danger" size="mini" plain icon="el-icon-delete" @click="handleDeleteTemplate(scope.row.id)" v-if="hasPermission('sys:notification:template:delete')">{{ $t('common.delete') }}</el-button>
                  </div>
                </template>
              </el-table-column>
            </el-table>
            <div class="notification-list-pagination">
              <el-pagination @size-change="handleTemplateSizeChange" @current-change="handleTemplateCurrentChange" :current-page="templatePagination.currentPage" :page-sizes="[10, 20, 50, 100]" :page-size="templatePagination.pageSize" layout="total, sizes, prev, pager, next, jumper" :total="templatePagination.total"></el-pagination>
            </div>
          </el-card>
        </div>
      </el-tab-pane>

      <el-tab-pane name="channels">
        <span slot="label">
          <i class="el-icon-phone-outline"></i> {{ $t('system.notificationChannel') }}
        </span>
        <div class="tab-content">
          <div class="control-panel">
            <div class="control-container">
              <div class="header-info">
                <i class="el-icon-info"></i>
                <span>{{ $t('system.notificationChannelTips') }}</span>
              </div>
            </div>
          </div>

          <div class="channel-list">
            <div class="channel-item" v-for="channel in channelList" :key="channel.channelName">
              <div class="channel-main">
                <div class="channel-icon">
                  <i :class="getChannelIcon(channel.channelName)"></i>
                </div>
                <div class="channel-info">
                  <div class="channel-header">
                    <h3 class="channel-title">{{ $t(getChannelLabel(channel.channelName)) }}</h3>
                    <el-switch v-model="channel.enabledBool" @change="handleChannelToggle(channel)" active-color="#13ce66" size="small" :disabled="!hasPermission('sys:notification:channel:update')"></el-switch>
                  </div>
                  <div class="channel-description">{{ $t(getChannelDescription(channel.channelName)) }}</div>
                </div>
              </div>

              <el-collapse-transition>
                <div v-if="channel.enabledBool && channel.channelName !== 'system'" class="channel-settings">
                  <el-divider></el-divider>
                  <el-form :ref="'channelForm_' + channel.channelName" :model="channel.settingsObj" label-width="120px" size="small">
                    <template v-if="channel.channelName === 'email'">
                      <el-row :gutter="20">
                        <el-col :span="12">
                          <el-form-item :label="$t('system.smtpHost')" prop="host">
                            <el-input v-model="channel.settingsObj.host" :placeholder="$t('system.enterSmtpHost')"></el-input>
                          </el-form-item>
                        </el-col>
                        <el-col :span="12">
                          <el-form-item :label="$t('system.smtpPort')" prop="port">
                            <el-input-number v-model="channel.settingsObj.port" :min="1" :max="65535" style="width: 100%"></el-input-number>
                          </el-form-item>
                        </el-col>
                      </el-row>
                      <el-row :gutter="20">
                        <el-col :span="12">
                          <el-form-item :label="$t('system.smtpUsername')" prop="username">
                            <el-input v-model="channel.settingsObj.username" :placeholder="$t('system.enterSmtpUsername')"></el-input>
                          </el-form-item>
                        </el-col>
                        <el-col :span="12">
                          <el-form-item :label="$t('system.smtpPassword')" prop="password">
                            <el-input v-model="channel.settingsObj.password" :placeholder="$t('system.enterSmtpPassword')" type="password" show-password></el-input>
                          </el-form-item>
                        </el-col>
                      </el-row>
                      <el-row :gutter="20">
                        <el-col :span="12">
                          <el-form-item :label="$t('system.smtpEncryption')" prop="encryption">
                            <el-select v-model="channel.settingsObj.encryption" :placeholder="$t('system.selectSmtpEncryption')" style="width: 100%">
                              <el-option :label="$t('system.noEncryption')" value="none"></el-option>
                              <el-option label="TLS" value="tls"></el-option>
                              <el-option label="SSL" value="ssl"></el-option>
                            </el-select>
                          </el-form-item>
                        </el-col>
                        <el-col :span="12">
                          <el-form-item :label="$t('system.senderEmail')" prop="from">
                            <el-input v-model="channel.settingsObj.from" :placeholder="$t('system.enterSenderEmail')"></el-input>
                          </el-form-item>
                        </el-col>
                      </el-row>
                    </template>

                    <template v-if="channel.channelName === 'sms'">
                      <el-row :gutter="20">
                        <el-col :span="12">
                          <el-form-item :label="$t('system.apiKey')" prop="apiKey">
                            <el-input v-model="channel.settingsObj.apiKey" :placeholder="$t('system.enterApiKey')" type="password" show-password></el-input>
                          </el-form-item>
                        </el-col>
                        <el-col :span="12">
                          <el-form-item :label="$t('system.apiSecret')" prop="apiSecret">
                            <el-input v-model="channel.settingsObj.apiSecret" :placeholder="$t('system.enterApiSecret')" type="password" show-password></el-input>
                          </el-form-item>
                        </el-col>
                      </el-row>
                      <el-form-item :label="$t('system.apiEndpoint')" prop="endpoint">
                        <el-input v-model="channel.settingsObj.endpoint" :placeholder="$t('system.enterApiEndpoint')"></el-input>
                      </el-form-item>
                      <el-form-item :label="$t('system.senderId')" prop="senderId">
                        <el-input v-model="channel.settingsObj.senderId" :placeholder="$t('system.enterSenderId')"></el-input>
                      </el-form-item>
                    </template>

                    <template v-if="channel.channelName === 'wechat'">
                      <el-row :gutter="20">
                        <el-col :span="12">
                          <el-form-item :label="$t('system.appId')" prop="appId">
                            <el-input v-model="channel.settingsObj.appId" :placeholder="$t('system.enterAppId')"></el-input>
                          </el-form-item>
                        </el-col>
                        <el-col :span="12">
                          <el-form-item :label="$t('system.appSecret')" prop="appSecret">
                            <el-input v-model="channel.settingsObj.appSecret" :placeholder="$t('system.enterAppSecret')" type="password" show-password></el-input>
                          </el-form-item>
                        </el-col>
                      </el-row>
                      <el-form-item :label="$t('system.templateId')" prop="templateId">
                        <el-input v-model="channel.settingsObj.templateId" :placeholder="$t('system.enterTemplateId')"></el-input>
                      </el-form-item>
                    </template>

                    <div class="channel-actions" v-if="hasPermission('sys:notification:channel:update')">
                      <el-button type="primary" icon="el-icon-check" @click="handleSaveChannel(channel)">{{ $t('common.save') }}</el-button>
                      <el-button type="success" :icon="getChannelTestIcon(channel.channelName)" @click="handleTestChannel(channel)">{{ $t(getChannelTestLabel(channel.channelName)) }}</el-button>
                    </div>
                  </el-form>
                </div>
              </el-collapse-transition>
            </div>
          </div>
        </div>
      </el-tab-pane>
    </el-tabs>

    <el-dialog :title="$t('system.notificationDetail')" :visible.sync="detailVisible" width="60%" :close-on-click-modal="false">
      <div class="notification-detail">
        <div class="detail-header">
          <h3 class="detail-title">{{ currentNotification.title }}</h3>
          <div class="detail-meta">
            <span class="meta-item"><i class="el-icon-time"></i> {{ formatDate(currentNotification.createDate) }}</span>
            <span class="meta-item"><i class="el-icon-user"></i> {{ currentNotification.sender }}</span>
            <span class="meta-item"><i class="el-icon-tickets"></i> {{ $t(getNotificationTypeKey(currentNotification.type)) }}</span>
            <el-tag :type="getNotificationStatusTagType(currentNotification.status)" effect="dark">{{ $t(getNotificationStatusKey(currentNotification.status)) }}</el-tag>
          </div>
        </div>
        <div class="detail-content">{{ currentNotification.content }}</div>
        <div class="detail-footer">
          <div class="detail-channels">
            <strong>{{ $t('system.channels') }}:</strong>
            <el-tag v-for="channel in parseChannels(currentNotification.channels)" :key="channel" size="mini" effect="plain" style="margin-left: 5px">{{ $t(getChannelKey(channel)) }}</el-tag>
          </div>
          <div class="detail-recipient">
            <strong>{{ $t('system.recipient') }}:</strong> {{ currentNotification.recipient || $t('system.allUsers') }}
          </div>
        </div>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="detailVisible = false">{{ $t('common.close') }}</el-button>
        <el-button v-if="hasPermission('sys:notification:update')" type="primary" @click="handleMarkAsRead(currentNotification)" :disabled="currentNotification.status === 'read' || currentNotification.status === 'deleted'">{{ $t('system.markAsRead') }}</el-button>
      </span>
    </el-dialog>

    <el-dialog :title="$t('system.sendNotification')" :visible.sync="sendVisible" width="50%" :close-on-click-modal="false">
      <el-form ref="sendForm" :model="sendForm" :rules="sendRules" label-position="top" label-width="80px">
        <el-form-item prop="title">
          <el-input v-model="sendForm.title" :placeholder="$t('system.enterNotificationTitle')" maxlength="100" show-word-limit></el-input>
        </el-form-item>
        <el-form-item prop="type">
          <el-select v-model="sendForm.type" :placeholder="$t('system.selectNotificationType')">
            <el-option v-for="type in notificationTypes" :key="type.value" :label="$t(type.label)" :value="type.value"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item prop="content">
          <el-input v-model="sendForm.content" type="textarea" :placeholder="$t('system.enterNotificationContent')" :rows="6" maxlength="1000" show-word-limit></el-input>
        </el-form-item>
        <el-form-item prop="channels">
          <el-checkbox-group v-model="sendForm.channels">
            <el-checkbox v-for="channel in notificationChannels" :key="channel.value" :label="channel.value">{{ $t(channel.label) }}</el-checkbox>
          </el-checkbox-group>
        </el-form-item>
        <el-form-item prop="recipient">
          <el-radio-group v-model="sendForm.recipientType">
            <el-radio label="all">{{ $t('system.allUsers') }}</el-radio>
            <el-radio label="specific">{{ $t('system.specificUser') }}</el-radio>
          </el-radio-group>
          <el-input v-if="sendForm.recipientType === 'specific'" v-model="sendForm.recipient" :placeholder="$t('system.enterRecipientUsername')" style="margin-top: 10px"></el-input>
        </el-form-item>
        <el-form-item prop="priority">
          <el-radio-group v-model="sendForm.priority">
            <el-radio label="low">{{ $t('system.lowPriority') }}</el-radio>
            <el-radio label="medium">{{ $t('system.mediumPriority') }}</el-radio>
            <el-radio label="high">{{ $t('system.highPriority') }}</el-radio>
          </el-radio-group>
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="sendVisible = false">{{ $t('common.cancel') }}</el-button>
        <el-button v-if="hasPermission('sys:notification:save')" type="primary" @click="handleSendNotificationConfirm"><i class="el-icon-message"></i>{{ $t('system.sendNotification') }}</el-button>
      </span>
    </el-dialog>

    <el-dialog :title="$t('system.editNotification')" :visible.sync="editVisible" width="50%" :close-on-click-modal="false">
      <el-form ref="editForm" :model="sendForm" :rules="sendRules" label-position="top" label-width="80px">
        <el-form-item prop="title">
          <el-input v-model="sendForm.title" :placeholder="$t('system.enterNotificationTitle')" maxlength="100" show-word-limit></el-input>
        </el-form-item>
        <el-form-item prop="type">
          <el-select v-model="sendForm.type" :placeholder="$t('system.selectNotificationType')">
            <el-option v-for="type in notificationTypes" :key="type.value" :label="$t(type.label)" :value="type.value"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item prop="content">
          <el-input v-model="sendForm.content" type="textarea" :placeholder="$t('system.enterNotificationContent')" :rows="6" maxlength="1000" show-word-limit></el-input>
        </el-form-item>
        <el-form-item prop="channels">
          <el-checkbox-group v-model="sendForm.channels">
            <el-checkbox v-for="channel in notificationChannels" :key="channel.value" :label="channel.value">{{ $t(channel.label) }}</el-checkbox>
          </el-checkbox-group>
        </el-form-item>
        <el-form-item prop="recipient">
          <el-radio-group v-model="sendForm.recipientType">
            <el-radio label="all">{{ $t('system.allUsers') }}</el-radio>
            <el-radio label="specific">{{ $t('system.specificUser') }}</el-radio>
          </el-radio-group>
          <el-input v-if="sendForm.recipientType === 'specific'" v-model="sendForm.recipient" :placeholder="$t('system.enterRecipientUsername')" style="margin-top: 10px"></el-input>
        </el-form-item>
        <el-form-item prop="priority">
          <el-radio-group v-model="sendForm.priority">
            <el-radio label="low">{{ $t('system.lowPriority') }}</el-radio>
            <el-radio label="medium">{{ $t('system.mediumPriority') }}</el-radio>
            <el-radio label="high">{{ $t('system.highPriority') }}</el-radio>
          </el-radio-group>
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="editVisible = false">{{ $t('common.cancel') }}</el-button>
        <el-button v-if="hasPermission('sys:notification:update')" type="primary" @click="handleEditNotificationConfirm"><i class="el-icon-edit"></i>{{ $t('system.updateNotification') }}</el-button>
      </span>
    </el-dialog>

    <el-dialog :title="templateDialogTitle" :visible.sync="templateDialogVisible" width="60%" :close-on-click-modal="false">
      <el-form ref="templateForm" :model="templateForm" :rules="templateRules" label-position="top" label-width="80px">
        <el-form-item prop="name">
          <el-input v-model="templateForm.name" :placeholder="$t('system.templateNamePlaceholder')"></el-input>
        </el-form-item>
        <el-form-item prop="type">
          <el-select v-model="templateForm.type" :placeholder="$t('system.selectTemplateType')">
            <el-option v-for="type in templateTypes" :key="type.value" :label="$t(type.label)" :value="type.value"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item prop="content">
          <el-input v-model="templateForm.content" type="textarea" rows="4" :placeholder="$t('system.templateContentPlaceholder')"></el-input>
          <div class="template-help-text">{{ $t('system.templateHelpText') }}</div>
        </el-form-item>
        <el-form-item prop="channels">
          <el-checkbox-group v-model="templateForm.channelsArr">
            <el-checkbox v-for="channel in notificationChannels" :key="channel.value" :label="channel.value">{{ $t(channel.label) }}</el-checkbox>
          </el-checkbox-group>
        </el-form-item>
        <el-form-item>
          <el-switch v-model="templateForm.enabledBool" active-color="#13ce66" inactive-color="#ff4949">{{ templateForm.enabledBool ? $t('common.enabled') : $t('common.disabled') }}</el-switch>
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="templateDialogVisible = false">{{ $t('common.cancel') }}</el-button>
        <el-button type="primary" @click="handleSaveTemplate">{{ $t('common.save') }}</el-button>
      </span>
    </el-dialog>

    <el-dialog :title="testDialogTitle" :visible.sync="testDialogVisible" width="500px" append-to-body>
      <el-form :model="testForm" label-position="top" size="medium">
        <el-form-item v-if="currentTestChannel === 'email'" :label="$t('system.recipientEmail')" prop="recipient">
          <el-input v-model="testForm.recipient" :placeholder="$t('system.enterRecipientEmail')" type="email" prefix-icon="el-icon-message"></el-input>
        </el-form-item>
        <el-form-item v-if="currentTestChannel === 'sms'" :label="$t('system.recipientPhone')" prop="recipient">
          <el-input v-model="testForm.recipient" :placeholder="$t('system.enterRecipientPhone')" type="tel" prefix-icon="el-icon-mobile-phone"></el-input>
        </el-form-item>
        <el-form-item v-if="currentTestChannel === 'wechat'" :label="$t('system.recipientOpenId')" prop="recipient">
          <el-input v-model="testForm.recipient" :placeholder="$t('system.enterRecipientOpenId')" prefix-icon="el-icon-user"></el-input>
        </el-form-item>
        <el-form-item :label="$t('system.testMessage')" prop="message">
          <el-input v-model="testForm.message" type="textarea" :rows="4" :placeholder="$t('system.enterTestMessage')"></el-input>
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="testDialogVisible = false" size="medium">{{ $t('common.cancel') }}</el-button>
        <el-button type="primary" @click="handleTestConfirm" size="medium">{{ $t('common.confirm') }}</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import {
  getNotifications,
  markNotificationAsRead,
  deleteNotification,
  createNotification,
  updateNotification,
  batchMarkNotificationsAsRead,
  batchMarkNotificationsAsUnread,
  batchDeleteNotifications,
  getNotificationTemplates,
  createNotificationTemplate,
  updateNotificationTemplate,
  deleteNotificationTemplate,
  toggleNotificationTemplate,
  getNotificationChannels,
  updateNotificationChannelConfig,
  testNotificationChannel
} from '@/apis/system/notification'
import { formatDate } from '@/utils/date'
import { hasPermission } from '@/utils/permission'

export default {
  name: 'NotificationManagement',
  data() {
    return {
      activeTab: 'notifications',

      notifications: [],
      pagination: { currentPage: 1, pageSize: 10, total: 0 },
      loading: false,
      searchForm: { keyword: '', type: '', status: '', priority: '' },
      dateRange: null,
      notificationTypes: [
        { value: 'system', label: 'system.systemNotification' },
        { value: 'alert', label: 'system.alertNotification' },
        { value: 'update', label: 'system.updateNotification' },
        { value: 'reminder', label: 'system.reminderNotification' },
        { value: 'info', label: 'system.infoNotification' }
      ],
      notificationPriorities: [
        { value: 'high', label: 'system.highPriority' },
        { value: 'medium', label: 'system.mediumPriority' },
        { value: 'low', label: 'system.lowPriority' }
      ],
      notificationStatuses: [
        { value: 'unread', label: 'system.unreadNotification' },
        { value: 'read', label: 'system.readNotification' },
        { value: 'deleted', label: 'system.deletedNotification' }
      ],
      notificationChannels: [
        { value: 'system', label: 'system.systemChannel' },
        { value: 'email', label: 'system.emailChannel' },
        { value: 'sms', label: 'system.smsChannel' },
        { value: 'wechat', label: 'system.wechatChannel' }
      ],
      sendForm: { title: '', type: 'info', content: '', channels: ['system'], recipientType: 'all', recipient: '', priority: 'medium' },
      detailVisible: false,
      sendVisible: false,
      editVisible: false,
      currentNotification: {},
      editingNotification: null,
      selectedNotifications: [],
      unreadCount: 0,

      templateList: [],
      templateLoading: false,
      selectedTemplateIds: [],
      templatePagination: { currentPage: 1, pageSize: 10, total: 0 },
      templateDialogVisible: false,
      isTemplateEdit: false,
      templateForm: { id: null, name: '', type: 'system', content: '', channelsArr: ['system'], enabledBool: true },
      templateTypes: [
        { value: 'system', label: 'system.systemNotification' },
        { value: 'alert', label: 'system.alertNotification' },
        { value: 'update', label: 'system.updateNotification' },
        { value: 'reminder', label: 'system.reminderNotification' },
        { value: 'info', label: 'system.infoNotification' }
      ],

      channelList: [],
      testDialogVisible: false,
      currentTestChannel: '',
      testForm: { recipient: '', message: '' }
    }
  },
  computed: {
    hasSelectedNotifications() { return this.selectedNotifications.length > 0 },
    hasBatchOperationPermission() { return this.hasPermission('sys:notification:update') || this.hasPermission('sys:notification:delete') },
    hasAnyNotificationAction() {
      return this.hasPermission('sys:notification:update') || this.hasPermission('sys:notification:info') || this.hasPermission('sys:notification:delete')
    },
    notificationActionWidth() {
      let count = 0
      if (this.hasPermission('sys:notification:update')) count += 2
      if (this.hasPermission('sys:notification:info')) count += 1
      if (this.hasPermission('sys:notification:delete')) count += 1
      const widthMap = { 1: 100, 2: 170, 3: 245, 4: 300 }
      return widthMap[count] || 0
    },
    hasAnyTemplateAction() {
      return this.hasPermission('sys:notification:template:update') || this.hasPermission('sys:notification:template:delete')
    },
    templateActionWidth() {
      let count = 0
      if (this.hasPermission('sys:notification:template:update')) count += 1
      if (this.hasPermission('sys:notification:template:delete')) count += 1
      const widthMap = { 1: 100, 2: 170 }
      return widthMap[count] || 0
    },
    sendRules() {
      return {
        title: [{ required: true, message: this.$t('system.enterNotificationTitle'), trigger: 'blur' }, { min: 5, max: 100, message: this.$t('system.notificationTitleLength'), trigger: 'blur' }],
        type: [{ required: true, message: this.$t('system.selectNotificationType'), trigger: 'change' }],
        content: [{ required: true, message: this.$t('system.enterNotificationContent'), trigger: 'blur' }, { min: 10, max: 1000, message: this.$t('system.notificationContentLength'), trigger: 'blur' }],
        channels: [{ required: true, message: this.$t('system.selectNotificationChannels'), type: 'array', trigger: 'change' }],
        recipient: [{ required: true, message: this.$t('system.enterRecipientUsername'), trigger: 'blur' }],
        priority: [{ required: true, message: this.$t('system.selectNotificationPriority'), trigger: 'change' }]
      }
    },
    templateDialogTitle() { return this.isTemplateEdit ? this.$t('system.editTemplate') : this.$t('system.createTemplate') },
    templateRules() {
      return {
        name: [{ required: true, message: this.$t('system.templateNameRequired'), trigger: 'blur' }],
        type: [{ required: true, message: this.$t('system.templateTypeRequired'), trigger: 'change' }],
        content: [{ required: true, message: this.$t('system.templateContentRequired'), trigger: 'blur' }],
        channelsArr: [{ type: 'array', required: true, message: this.$t('system.templateChannelsRequired'), trigger: 'change' }]
      }
    },
    testDialogTitle() {
      const map = { email: this.$t('system.testEmail'), sms: this.$t('system.testSms'), wechat: this.$t('system.testWechat') }
      return map[this.currentTestChannel] || this.$t('system.testNotification')
    }
  },
  mounted() {
    this.getNotifications()
  },
  methods: {
    hasPermission,
    formatDate,

    parseChannels(channels) {
      if (Array.isArray(channels)) return channels
      if (typeof channels === 'string' && channels) return channels.split(',').map(c => c.trim()).filter(c => c)
      return []
    },

    handleTabClick(tab) {
      if (tab.name === 'templates' && this.templateList.length === 0) {
        this.fetchTemplates()
      } else if (tab.name === 'channels' && this.channelList.length === 0) {
        this.fetchChannels()
      }
    },

    getNotificationTypeKey(type) {
      const m = { system: 'system.systemNotification', alert: 'system.alertNotification', update: 'system.updateNotification', reminder: 'system.reminderNotification', info: 'system.infoNotification' }
      return m[type] || type
    },
    getNotificationTypeTagType(type) {
      const m = { system: 'info', alert: 'danger', update: 'success', reminder: 'warning', info: 'primary' }
      return m[type] || ''
    },
    getNotificationStatusKey(status) {
      const m = { unread: 'system.unreadNotification', read: 'system.readNotification', deleted: 'system.deletedNotification' }
      return m[status] || status
    },
    getNotificationStatusTagType(status) {
      const m = { unread: 'warning', read: 'success', deleted: 'danger' }
      return m[status] || ''
    },
    getChannelKey(channel) {
      const m = { system: 'system.systemChannel', email: 'system.emailChannel', sms: 'system.smsChannel', wechat: 'system.wechatChannel' }
      return m[channel] || channel
    },
    getPriorityKey(priority) {
      const m = { high: 'system.highPriority', medium: 'system.mediumPriority', low: 'system.lowPriority' }
      return m[priority] || priority
    },
    getPriorityTagType(priority) {
      const m = { high: 'danger', medium: 'warning', low: 'info' }
      return m[priority] || ''
    },
    getTemplateTypeText(type) {
      const m = { system: 'system.systemNotification', alert: 'system.alertNotification', update: 'system.updateNotification', reminder: 'system.reminderNotification', info: 'system.infoNotification' }
      return m[type] || type
    },
    getChannelIcon(name) {
      const m = { system: 'el-icon-bell', email: 'el-icon-message', sms: 'el-icon-chat-dot-square', wechat: 'el-icon-chat-dot-round' }
      return m[name] || 'el-icon-phone-outline'
    },
    getChannelLabel(name) {
      const m = { system: 'system.systemChannel', email: 'system.emailChannel', sms: 'system.smsChannel', wechat: 'system.wechatChannel' }
      return m[name] || name
    },
    getChannelDescription(name) {
      const m = { system: 'system.systemChannelDescription', email: 'system.emailChannelDescription', sms: 'system.smsChannelDescription', wechat: 'system.wechatChannelDescription' }
      return m[name] || ''
    },
    getChannelTestIcon(name) {
      const m = { email: 'el-icon-position', sms: 'el-icon-mobile-phone', wechat: 'el-icon-chat-round' }
      return m[name] || 'el-icon-position'
    },
    getChannelTestLabel(name) {
      const m = { email: 'system.testEmail', sms: 'system.testSms', wechat: 'system.testWechat' }
      return m[name] || 'system.testNotification'
    },

    async getNotifications() {
      this.loading = true
      try {
        const params = { page: this.pagination.currentPage, limit: this.pagination.pageSize, keyword: this.searchForm.keyword, type: this.searchForm.type, status: this.searchForm.status, priority: this.searchForm.priority }
        if (this.dateRange) { params.startTime = this.dateRange[0]; params.endTime = this.dateRange[1] }
        const response = await getNotifications(params)
        if (response) {
          const pageData = response
          this.notifications = (pageData.list || pageData.data || []) 
          this.pagination.total = pageData.total || 0
        } else { this.notifications = []; this.pagination.total = 0 }
        this.calculateUnreadCount()
      } catch (error) {
        this.$message.error(this.$t('system.getNotificationsFailed'))
        this.notifications = []; this.pagination.total = 0
      } finally { this.loading = false }
    },
    handleSizeChange(val) { this.pagination.pageSize = val; this.getNotifications() },
    handleCurrentChange(val) { this.pagination.currentPage = val; this.getNotifications() },
    handleSearch() { this.pagination.currentPage = 1; this.getNotifications() },
    handleReset() { this.searchForm = { keyword: '', type: '', status: '', priority: '' }; this.dateRange = null; this.pagination.currentPage = 1; this.getNotifications() },
    handleSelectionChange(selection) { this.selectedNotifications = selection },
    handleViewDetail(notification) { this.currentNotification = notification; this.detailVisible = true },
    handleMarkAsRead(notification) {
      this.$confirm(this.$t('system.markAsReadConfirm'), this.$t('common.confirm'), { confirmButtonText: this.$t('common.confirm'), cancelButtonText: this.$t('common.cancel'), type: 'warning' }).then(async () => {
        try {
          await markNotificationAsRead(notification.id)
          const idx = this.notifications.findIndex(item => item.id === notification.id)
          if (idx !== -1) this.notifications[idx].status = 'read'
          if (this.currentNotification.id === notification.id) this.currentNotification.status = 'read'
          this.calculateUnreadCount()
          this.$message.success(this.$t('system.markAsReadSuccess'))
        } catch (error) { this.$message.error(this.$t('system.markAsReadFailed')) }
      }).catch(() => {})
    },
    handleBatchMarkAsRead() {
      if (this.selectedNotifications.length === 0) { this.$message.warning(this.$t('system.selectNotificationsFirst')); return }
      this.$confirm(this.$t('system.batchMarkAsReadConfirm'), this.$t('common.confirm'), { confirmButtonText: this.$t('common.confirm'), cancelButtonText: this.$t('common.cancel'), type: 'warning' }).then(async () => {
        try {
          const unread = this.selectedNotifications.filter(item => item.status === 'unread')
          if (unread.length === 0) { this.$message.warning(this.$t('system.noUnreadNotificationsSelected')); return }
          await batchMarkNotificationsAsRead(unread.map(item => item.id))
          unread.forEach(n => { const idx = this.notifications.findIndex(item => item.id === n.id); if (idx !== -1) this.notifications[idx].status = 'read' })
          this.selectedNotifications = []; this.calculateUnreadCount()
          this.$message.success(this.$t('system.batchMarkAsReadSuccess'))
        } catch (error) { this.$message.error(this.$t('system.batchMarkAsReadFailed')) }
      }).catch(() => {})
    },
    calculateUnreadCount() { this.unreadCount = this.notifications.filter(item => item.status === 'unread').length },
    handleViewUnreadOnly() { this.searchForm.status = 'unread'; this.pagination.currentPage = 1; this.getNotifications() },
    handleDelete(notification) {
      this.$confirm(this.$t('system.deleteNotificationConfirm'), this.$t('common.confirm'), { confirmButtonText: this.$t('common.confirm'), cancelButtonText: this.$t('common.cancel'), type: 'error' }).then(async () => {
        try {
          await deleteNotification(notification.id)
          const idx = this.notifications.findIndex(item => item.id === notification.id)
          if (idx !== -1) { this.notifications.splice(idx, 1); this.pagination.total-- }
          if (this.currentNotification.id === notification.id) this.detailVisible = false
          this.$message.success(this.$t('system.deleteNotificationSuccess'))
        } catch (error) { this.$message.error(this.$t('system.deleteNotificationFailed')) }
      }).catch(() => {})
    },
    handleBatchDelete() {
      if (this.selectedNotifications.length === 0) { this.$message.warning(this.$t('system.selectNotificationsFirst')); return }
      this.$confirm(this.$t('system.batchDeleteNotificationConfirm'), this.$t('common.confirm'), { confirmButtonText: this.$t('common.confirm'), cancelButtonText: this.$t('common.cancel'), type: 'error' }).then(async () => {
        try {
          await batchDeleteNotifications(this.selectedNotifications.map(item => item.id))
          this.selectedNotifications.forEach(n => { const idx = this.notifications.findIndex(item => item.id === n.id); if (idx !== -1) { this.notifications.splice(idx, 1); this.pagination.total-- } })
          this.selectedNotifications = []
          this.$message.success(this.$t('system.batchDeleteNotificationSuccess'))
        } catch (error) { this.$message.error(this.$t('system.batchDeleteNotificationFailed')) }
      }).catch(() => {})
    },
    handleCreate() {
      this.sendForm = { title: '', type: 'info', content: '', channels: ['system'], recipientType: 'all', recipient: '', priority: 'medium' }
      this.sendVisible = true
    },
    handleSendNotificationConfirm() {
      this.$refs.sendForm.validate(async (valid) => {
        if (valid) {
          try {
            const data = { title: this.sendForm.title, type: this.sendForm.type, content: this.sendForm.content, channels: this.sendForm.channels ? this.sendForm.channels.join(',') : '', recipient: this.sendForm.recipientType === 'specific' ? this.sendForm.recipient : null, priority: this.sendForm.priority }
            await createNotification(data)
            this.$message.success(this.$t('system.sendNotificationSuccess'))
            this.sendVisible = false; this.getNotifications()
          } catch (error) { this.$message.error(this.$t('system.sendNotificationFailed')) }
        }
      })
    },
    handleEditNotification(notification) {
      this.editingNotification = { ...notification }
      this.sendForm = { title: notification.title, type: notification.type, content: notification.content, channels: this.parseChannels(notification.channels), recipientType: notification.recipient ? 'specific' : 'all', recipient: notification.recipient || '', priority: notification.priority || 'medium' }
      this.editVisible = true
    },
    handleEditNotificationConfirm() {
      this.$refs.editForm.validate(async (valid) => {
        if (valid) {
          try {
            const data = { title: this.sendForm.title, type: this.sendForm.type, content: this.sendForm.content, channels: this.sendForm.channels ? this.sendForm.channels.join(',') : '', recipient: this.sendForm.recipientType === 'specific' ? this.sendForm.recipient : null, priority: this.sendForm.priority }
            await updateNotification(this.editingNotification.id, data)
            this.$message.success(this.$t('system.updateNotificationSuccess'))
            this.editVisible = false; this.getNotifications()
          } catch (error) { this.$message.error(this.$t('system.updateNotificationFailed')) }
        }
      })
    },

    async fetchTemplates() {
      this.templateLoading = true
      try {
        const params = { page: this.templatePagination.currentPage, limit: this.templatePagination.pageSize }
        const response = await getNotificationTemplates(params)
        if (response) {
          const pageData = response
          const list = pageData.list || pageData.data || []
          this.templateList = list.map(t => ({ ...t, enabledBool: t.enabled === 1 || t.enabled === true }))
          this.templatePagination.total = pageData.total || 0
        } else { this.templateList = []; this.templatePagination.total = 0 }
      } catch (error) { console.error('Failed to fetch templates:', error); this.templateList = [] }
      finally { this.templateLoading = false }
    },
    handleTemplateSizeChange(val) { this.templatePagination.pageSize = val; this.fetchTemplates() },
    handleTemplateCurrentChange(val) { this.templatePagination.currentPage = val; this.fetchTemplates() },
    handleTemplateSelectionChange(selection) { this.selectedTemplateIds = selection.map(item => item.id) },
    handleCreateTemplate() {
      this.isTemplateEdit = false
      this.templateForm = { id: null, name: '', type: 'system', content: '', channelsArr: ['system'], enabledBool: true }
      this.templateDialogVisible = true
      this.$nextTick(() => { this.$refs.templateForm?.clearValidate() })
    },
    handleEditTemplate(row) {
      this.isTemplateEdit = true
      this.templateForm = { ...row, channelsArr: this.parseChannels(row.channels), enabledBool: row.enabled === 1 || row.enabled === true }
      this.templateDialogVisible = true
      this.$nextTick(() => { this.$refs.templateForm?.clearValidate() })
    },
    async handleSaveTemplate() {
      this.$refs.templateForm.validate(async (valid) => {
        if (valid) {
          try {
            const data = { name: this.templateForm.name, type: this.templateForm.type, content: this.templateForm.content, channels: this.templateForm.channelsArr.join(','), enabled: this.templateForm.enabledBool ? 1 : 0 }
            if (this.isTemplateEdit) {
              await updateNotificationTemplate(this.templateForm.id, data)
              this.$message.success(this.$t('common.updateSuccess'))
            } else {
              await createNotificationTemplate(data)
              this.$message.success(this.$t('common.createSuccess'))
            }
            this.templateDialogVisible = false; this.fetchTemplates()
          } catch (error) { console.error('Failed to save template:', error) }
        }
      })
    },
    async handleDeleteTemplate(id) {
      try {
        await this.$confirm(this.$t('system.confirmDeleteTemplate'), this.$t('common.warning'), { confirmButtonText: this.$t('common.confirm'), cancelButtonText: this.$t('common.cancel'), type: 'warning' })
        await deleteNotificationTemplate(id)
        this.$message.success(this.$t('common.deleteSuccess')); this.fetchTemplates()
      } catch (error) { if (error !== 'cancel') console.error('Failed to delete template:', error) }
    },
    async handleToggleTemplateEnable(row) {
      try {
        await toggleNotificationTemplate(row.id, row.enabledBool)
        this.$message.success(row.enabledBool ? this.$t('common.enabled') : this.$t('common.disabled'))
      } catch (error) { row.enabledBool = !row.enabledBool; console.error('Failed to toggle template:', error) }
    },
    async handleBatchEnableTemplates() {
      try {
        await this.$confirm(this.$t('system.confirmBatchEnableTemplates'), this.$t('common.warning'), { confirmButtonText: this.$t('common.confirm'), cancelButtonText: this.$t('common.cancel'), type: 'warning' })
        this.templateLoading = true
        await Promise.all(this.selectedTemplateIds.map(id => toggleNotificationTemplate(id, true)))
        this.$message.success(this.$t('common.operationSuccess')); this.fetchTemplates()
      } catch (error) { if (error !== 'cancel') { console.error('Failed to batch enable templates:', error); this.$message.error(this.$t('common.operationFailed')) } }
      finally { this.templateLoading = false }
    },
    async handleBatchDisableTemplates() {
      try {
        await this.$confirm(this.$t('system.confirmBatchDisableTemplates'), this.$t('common.warning'), { confirmButtonText: this.$t('common.confirm'), cancelButtonText: this.$t('common.cancel'), type: 'warning' })
        this.templateLoading = true
        await Promise.all(this.selectedTemplateIds.map(id => toggleNotificationTemplate(id, false)))
        this.$message.success(this.$t('common.operationSuccess')); this.fetchTemplates()
      } catch (error) { if (error !== 'cancel') { console.error('Failed to batch disable templates:', error); this.$message.error(this.$t('common.operationFailed')) } }
      finally { this.templateLoading = false }
    },
    async handleBatchDeleteTemplates() {
      try {
        await this.$confirm(this.$t('system.confirmBatchDeleteTemplates'), this.$t('common.warning'), { confirmButtonText: this.$t('common.confirm'), cancelButtonText: this.$t('common.cancel'), type: 'warning' })
        this.templateLoading = true
        await Promise.all(this.selectedTemplateIds.map(id => deleteNotificationTemplate(id)))
        this.$message.success(this.$t('common.deleteSuccess')); this.fetchTemplates()
      } catch (error) { if (error !== 'cancel') { console.error('Failed to batch delete templates:', error); this.$message.error(this.$t('common.operationFailed')) } }
      finally { this.templateLoading = false }
    },

    async fetchChannels() {
      try {
        const response = await getNotificationChannels()
        if (response) {
          const pageData = response
          const list = pageData.list || pageData.data || (Array.isArray(pageData) ? pageData : [])
          this.channelList = list.map(c => {
            let settingsObj = {}
            try { settingsObj = typeof c.settings === 'string' ? JSON.parse(c.settings || '{}') : (c.settings || {}) } catch (e) { settingsObj = {} }
            return { ...c, enabledBool: c.enabled === 1 || c.enabled === true, settingsObj }
          })
        }
        if (this.channelList.length === 0) {
          this.channelList = this.getDefaultChannels()
        }
      } catch (error) {
        console.error('Failed to fetch channels:', error)
        this.channelList = this.getDefaultChannels()
      }
    },
    getDefaultChannels() {
      return [
        { channelName: 'system', enabledBool: true, settingsObj: {}, description: '系统通知渠道' },
        { channelName: 'email', enabledBool: false, settingsObj: { host: '', port: 587, username: '', password: '', encryption: 'none', from: '', senderName: '' }, description: '邮件通知渠道' },
        { channelName: 'sms', enabledBool: false, settingsObj: { apiKey: '', apiSecret: '', endpoint: '', senderId: '' }, description: '短信通知渠道' },
        { channelName: 'wechat', enabledBool: false, settingsObj: { appId: '', appSecret: '', templateId: '' }, description: '微信通知渠道' }
      ]
    },
    async handleChannelToggle(channel) {
      try {
        await updateNotificationChannelConfig(channel.channelName, { enabled: channel.enabledBool, settings: JSON.stringify(channel.settingsObj) })
        this.$message.success(this.$t('system.updateChannelSuccess'))
      } catch (error) {
        channel.enabledBool = !channel.enabledBool
        this.$message.error(this.$t('system.updateChannelFailed'))
      }
    },
    async handleSaveChannel(channel) {
      try {
        await updateNotificationChannelConfig(channel.channelName, { enabled: channel.enabledBool, settings: JSON.stringify(channel.settingsObj) })
        this.$message.success(this.$t('system.saveChannelSuccess'))
      } catch (error) { this.$message.error(this.$t('system.saveChannelFailed')) }
    },
    handleTestChannel(channel) {
      this.currentTestChannel = channel.channelName
      this.testForm = { recipient: '', message: this.$t('system.testNotificationMessage') }
      this.testDialogVisible = true
    },
    async handleTestConfirm() {
      if (!this.testForm.recipient) { this.$message.warning(this.$t('system.enterRecipient')); return }
      if (!this.testForm.message) { this.$message.warning(this.$t('system.enterTestMessage')); return }
      try {
        await testNotificationChannel(this.currentTestChannel, { recipient: this.testForm.recipient, message: this.testForm.message })
        this.$message.success(this.$t('system.testNotificationSuccess'))
        this.testDialogVisible = false
      } catch (error) { this.$message.error(this.$t('system.testNotificationFailed')) }
    }
  }
}
</script>

<style scoped lang="scss">
@import "@/styles/list-page.scss";

/* 通知管理页面特有样式 */
.list-page-container {
  .notification-tabs {
    height: 100%;
    display: flex;
    flex-direction: column;

    :deep(.el-tabs__header) {
      margin: 0 0 12px;
      border-bottom: 2px solid #f1f5f9;
      padding: 0 4px;

      .el-tabs__nav-wrap::after {
        display: none;
      }

      .el-tabs__item {
        height: 46px;
        line-height: 46px;
        font-size: 14px;
        font-weight: 500;
        color: #64748b;
        padding: 0 20px;
        transition: all 0.25s;

        i {
          margin-right: 6px;
          font-size: 16px;
        }

        &:hover {
          color: #409eff;
        }

        &.is-active {
          color: #409eff;
          font-weight: 600;
        }
      }

      .el-tabs__active-bar {
        height: 3px;
        border-radius: 3px 3px 0 0;
        background-color: #409eff;
      }
    }

    :deep(.el-tabs__content) {
      flex: 1;
      overflow: hidden;
    }

    :deep(.el-tab-pane) {
      height: 100%;
    }
  }

  .tab-content {
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  .filter-input {
    width: 240px;
  }

  .notification-list-pagination {
    padding: 20px 0 5px;
    display: flex;
    justify-content: flex-end;
  }

  .notification-title {
    cursor: pointer;

    .unread {
      font-weight: 600;
      color: #409EFF;
    }
  }

  .notification-content {
    cursor: pointer;
    color: #64748b;
  }

  .notification-detail {
    padding: 10px 0;
  }

  .detail-header {
    margin-bottom: 20px;
  }

  .detail-title {
    font-size: 18px;
    font-weight: 600;
    margin-bottom: 12px;
    color: #1e293b;
  }

  .detail-meta {
    display: flex;
    align-items: center;
    gap: 16px;
    color: #64748b;
    font-size: 13px;
    flex-wrap: wrap;
  }

  .meta-item {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .detail-content {
    margin-bottom: 24px;
    line-height: 1.6;
    color: #334155;
    font-size: 15px;
    white-space: pre-wrap;
    padding: 16px;
    background-color: #f8fafc;
    border-radius: 8px;
  }

  .detail-footer {
    padding-top: 16px;
    border-top: 1px solid #f1f5f9;
    font-size: 13px;
    color: #64748b;
  }

  .detail-channels, .detail-recipient {
    margin-bottom: 8px;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .action-buttons {
    .el-button {
      &:hover {
        transform: translateY(-1px);
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
      }
    }

    .delete-btn {
      color: #ef4444;
      &:hover { color: #dc2626; }
    }
  }

  .template-content-preview {
    color: #64748b;
    font-size: 13px;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    overflow: hidden;
    line-height: 1.5;
  }

  .channel-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    justify-content: center;
  }

  .channel-tag {
    border-radius: 4px;
    border: 1px solid #e2e8f0;
    color: #64748b;
  }

  .template-help-text {
    font-size: 12px;
    color: #94a3b8;
    margin-top: 8px;
    line-height: 1.5;
  }

  .channel-list {
    flex: 1;
    overflow-y: auto;
    padding: 0 0 16px;
  }

  .channel-item {
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    padding: 20px 24px;
    margin-bottom: 12px;
    background-color: #fff;
    transition: all 0.25s;

    &:hover {
      border-color: #cbd5e1;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
    }
  }

  .channel-main {
    display: flex;
    align-items: center;
    gap: 16px;
  }

  .channel-icon {
    width: 48px;
    height: 48px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #eff6ff, #dbeafe);
    flex-shrink: 0;

    i {
      font-size: 24px;
      color: #3b82f6;
    }
  }

  .channel-info {
    flex: 1;
    min-width: 0;
  }

  .channel-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 4px;
  }

  .channel-title {
    font-size: 15px;
    font-weight: 600;
    color: #1e293b;
    margin: 0;
  }

  .channel-description {
    font-size: 13px;
    color: #64748b;
    line-height: 1.5;
  }

  .channel-settings {
    margin-top: 4px;
  }

  .channel-actions {
    display: flex;
    gap: 10px;
    margin-top: 16px;
    padding-top: 12px;
    border-top: 1px solid #f1f5f9;
  }

  :deep(.el-dialog) {
    border-radius: 12px;
    overflow: hidden;

    .el-dialog__header {
      background-color: #f8fafc;
      border-bottom: 1px solid #f1f5f9;
      padding: 20px;

      .el-dialog__title {
        font-size: 16px;
        font-weight: 600;
        color: #1e293b;
      }
    }

    .el-dialog__body {
      padding: 24px;
    }

    .el-dialog__footer {
      padding: 15px 24px 24px;
      border-top: 1px solid #f1f5f9;
    }
  }
}

@media screen and (max-width: 1400px) {
  .list-page-container .action-buttons .el-button {
    min-width: 50px;
    height: 28px;
    font-size: 12px;
    padding: 0 8px;
  }
}

@media screen and (max-width: 768px) {
  .list-page-container {
    padding: 5px;

    .channel-main {
      flex-wrap: wrap;
    }

    .channel-icon {
      width: 40px;
      height: 40px;

      i { font-size: 20px; }
    }
  }
}
</style>
